import { Loader } from '@googlemaps/js-api-loader';

interface IGoogleMapsService {
    initializeGoogleMaps(): Promise<void>;
    getGeocode(address: string): Promise<google.maps.GeocoderResult | null>;
    getPlacePredictions(input: string, searchBounds?: google.maps.LatLngBounds, country?: string): Promise<google.maps.places.AutocompletePrediction[]>;
    getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult | null>;
}

/**
 * Normalizes country name or code to ISO 3166-1 alpha-2 format (2-letter country code)
 * Examples: "United States" -> "US", "Canada" -> "CA", "US" -> "US"
 */
function normalizeCountryCode(country: string | undefined): string | undefined {
    if (!country) return undefined;
    
    const trimmed = country.trim().toUpperCase();
    
    // If already a 2-letter code, return it
    if (trimmed.length === 2 && /^[A-Z]{2}$/.test(trimmed)) {
        return trimmed;
    }
    
    // Map of common country names to ISO codes
    const countryMap: Record<string, string> = {
        'UNITED STATES': 'US',
        'USA': 'US',
        'UNITED STATES OF AMERICA': 'US',
        'CANADA': 'CA',
        'UNITED KINGDOM': 'GB',
        'UK': 'GB',
        'GREAT BRITAIN': 'GB',
        'AUSTRALIA': 'AU',
        'NEW ZEALAND': 'NZ',
        'MEXICO': 'MX',
        'GERMANY': 'DE',
        'FRANCE': 'FR',
        'SPAIN': 'ES',
        'ITALY': 'IT',
        'JAPAN': 'JP',
        'CHINA': 'CN',
        'INDIA': 'IN',
        'BRAZIL': 'BR',
        'ARGENTINA': 'AR',
        'CHILE': 'CL',
        'SOUTH AFRICA': 'ZA',
        'NETHERLANDS': 'NL',
        'BELGIUM': 'BE',
        'SWITZERLAND': 'CH',
        'SWEDEN': 'SE',
        'NORWAY': 'NO',
        'DENMARK': 'DK',
        'FINLAND': 'FI',
        'POLAND': 'PL',
        'IRELAND': 'IE',
        'PORTUGAL': 'PT',
        'AUSTRIA': 'AT',
        'GREECE': 'GR',
        'TURKEY': 'TR',
        'RUSSIA': 'RU',
        'SOUTH KOREA': 'KR',
        'SINGAPORE': 'SG',
        'HONG KONG': 'HK',
        'THAILAND': 'TH',
        'MALAYSIA': 'MY',
        'INDONESIA': 'ID',
        'PHILIPPINES': 'PH',
        'VIETNAM': 'VN',
        'ISRAEL': 'IL',
        'EGYPT': 'EG',
        'SAUDI ARABIA': 'SA',
        'UAE': 'AE',
        'UNITED ARAB EMIRATES': 'AE'
    };
    
    const code = countryMap[trimmed];
    if (code) {
        console.log(`Normalized country "${country}" to ISO code "${code}"`);
        return code;
    }
    
    // If no mapping found, log a warning and return undefined
    console.warn(`Could not normalize country "${country}" to ISO 3166-1 alpha-2 code. Autocomplete will not be country-restricted.`);
    return undefined;
}

class GoogleMapsService implements IGoogleMapsService {
    private static instance: GoogleMapsService;
    private loader: Loader | null = null;
    private geocoder: google.maps.Geocoder | null = null;
    private geocodeCache: Map<string, google.maps.GeocoderResult> = new Map();
    private placeCache: Map<string, google.maps.places.PlaceResult> = new Map();
    private isInitialized: boolean = false;
    private initializationPromise: Promise<void> | null = null;

    private constructor() {
        // Private constructor for singleton pattern
    }

    public static getInstance(): GoogleMapsService {
        if (!GoogleMapsService.instance) {
            GoogleMapsService.instance = new GoogleMapsService();
        }
        return GoogleMapsService.instance;
    }

    public async initializeGoogleMaps(): Promise<void> {
        // If already initialized, return immediately
        if (this.isInitialized) return;

        // If initialization is in progress, wait for it to complete
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // Start initialization
        this.initializationPromise = this.doInitialize();
        await this.initializationPromise;
    }

    private async doInitialize(): Promise<void> {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
            console.error('Google Maps API key is not set. Please add your API key to the .env file.');
            throw new Error('Google Maps API key is not set');
        }

        try {
            this.loader = new Loader({
                apiKey,
                version: 'weekly',
                libraries: ['places', 'geocoding', 'maps', 'marker']
            });

            // Load all libraries
            await this.loader.load();

            this.geocoder = new google.maps.Geocoder();

            this.isInitialized = true;
            console.log('Google Maps initialized successfully');
        } catch (error) {
            console.error('Error loading Google Maps:', error);
            this.initializationPromise = null; // Reset so we can retry
            throw error;
        }
    }

    public async getGeocode(address: string): Promise<google.maps.GeocoderResult | null> {
        if (!this.geocoder) {
            await this.initializeGoogleMaps();
        }

        // Check cache first
        const cachedResult = this.geocodeCache.get(address);
        if (cachedResult) {
            return cachedResult;
        }

        try {
            const response = await this.geocoder!.geocode({ address });
            if (response.results.length > 0) {
                // Cache the result
                this.geocodeCache.set(address, response.results[0]);
                return response.results[0];
            }
            return null;
        } catch (error) {
            console.error('Geocoding error:', error);
            return null;
        }
    }

    public async getPlacePredictions(
        input: string,
        searchBounds?: google.maps.LatLngBounds,
        country?: string
    ): Promise<google.maps.places.AutocompletePrediction[]> {
        if (!this.isInitialized) {
            await this.initializeGoogleMaps();
        }

        try {
            // Build the request for the new AutocompleteSuggestion API
            const request: google.maps.places.AutocompleteRequest = {
                input,
                includedPrimaryTypes: ['street_address', 'subpremise', 'premise', 'route'],
            };

            // Country RESTRICTION: Only show results from this country (hard filter)
            const normalizedCountry = normalizeCountryCode(country);
            if (normalizedCountry) {
                console.log(`GoogleMapsService: RESTRICTING results to country: ${normalizedCountry}`);
                request.includedRegionCodes = [normalizedCountry];
            } else if (country) {
                console.warn(`GoogleMapsService: Country "${country}" could not be normalized. No country restriction applied.`);
            }

            // City BIASING: Rank results from this city higher, but don't restrict to it
            if (searchBounds) {
                console.log('GoogleMapsService: BIASING results toward city bounds (will rank higher but not restrict):', {
                    sw: searchBounds.getSouthWest().toJSON(),
                    ne: searchBounds.getNorthEast().toJSON()
                });
                request.locationBias = searchBounds;
            }

            console.log('GoogleMapsService: Making autocomplete request with:', {
                input,
                hasCountryRestriction: !!request.includedRegionCodes,
                countryCode: request.includedRegionCodes?.[0],
                hasCityBias: !!request.locationBias,
                types: request.includedPrimaryTypes
            });

            // Use the new AutocompleteSuggestion API
            const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            console.log(`GoogleMapsService: Received ${suggestions.length} suggestions`);

            // Convert new API response to legacy AutocompletePrediction format for compatibility
            const predictions: google.maps.places.AutocompletePrediction[] = suggestions
                .filter(suggestion => suggestion.placePrediction)
                .map(suggestion => {
                    const placePrediction = suggestion.placePrediction!;
                    return {
                        description: placePrediction.text.text,
                        place_id: placePrediction.placeId,
                        structured_formatting: {
                            main_text: placePrediction.mainText?.text || '',
                            main_text_matched_substrings: placePrediction.mainText?.matches?.map(m => ({
                                offset: m.startOffset || 0,
                                length: (m.endOffset || 0) - (m.startOffset || 0)
                            })) || [],
                            secondary_text: placePrediction.secondaryText?.text || ''
                        },
                        matched_substrings: placePrediction.text.matches?.map(m => ({
                            offset: m.startOffset || 0,
                            length: (m.endOffset || 0) - (m.startOffset || 0)
                        })) || [],
                        terms: [],
                        types: placePrediction.types || []
                    };
                });

            return predictions;
        } catch (error) {
            console.error('Place predictions error:', error);
            return [];
        }
    }

    public async getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult | null> {
        if (!this.isInitialized) {
            await this.initializeGoogleMaps();
        }

        // Check cache first
        const cachedResult = this.placeCache.get(placeId);
        if (cachedResult) {
            return cachedResult;
        }

        try {
            // Use the new Place class API
            const place = new google.maps.places.Place({ id: placeId });
            await place.fetchFields({ fields: ['formattedAddress', 'location', 'displayName', 'addressComponents'] });

            // Convert to legacy PlaceResult format for compatibility
            const result: google.maps.places.PlaceResult = {
                formatted_address: place.formattedAddress || undefined,
                geometry: place.location ? {
                    location: place.location
                } : undefined,
                name: place.displayName || undefined,
                place_id: placeId,
                address_components: place.addressComponents?.map(component => ({
                    long_name: component.longText || '',
                    short_name: component.shortText || '',
                    types: component.types || []
                }))
            };

            // Cache the result
            this.placeCache.set(placeId, result);
            return result;
        } catch (error) {
            console.error('Place details error:', error);
            return null;
        }
    }
}

export const googleMapsService = GoogleMapsService.getInstance();
